import type { Tax } from '@monorepo/shared/schemas/taxes'
import { TAX_TYPE } from '@monorepo/shared/schemas/taxes'
import type { ColumnDef } from '@tanstack/react-table'
import {
  DollarSign,
  Edit,
  MoreHorizontal,
  Pause,
  Percent,
  Play,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { EnhancedTable } from '@/shared/components/table/EnhancedTable'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { useDeleteTax, useUpdateTax } from '../hooks/useTaxes'

interface TaxesTableProps {
  taxes: Tax[]
  onEditTax?: (tax: Tax) => void
  onViewTax?: (tax: Tax) => void
}

export function TaxesTable({ taxes, onEditTax, onViewTax }: TaxesTableProps) {
  const [deleteTax, setDeleteTax] = useState<Tax | null>(null)
  const deleteTaxMutation = useDeleteTax()
  const updateTaxMutation = useUpdateTax()

  const handleTaxAction = async (action: string, tax: Tax) => {
    switch (action) {
      case 'view': {
        onViewTax?.(tax)
        break
      }
      case 'edit': {
        onEditTax?.(tax)
        break
      }
      case 'activate': {
        try {
          await updateTaxMutation.mutateAsync({
            id: tax.id,
            data: { is_active: true },
          })
        } catch (error) {
          toast.error(extractErrorMessage(error))
        }
        break
      }
      case 'deactivate': {
        try {
          await updateTaxMutation.mutateAsync({
            id: tax.id,
            data: { is_active: false },
          })
        } catch (error) {
          toast.error(extractErrorMessage(error))
        }
        break
      }
      case 'delete': {
        setDeleteTax(tax)
        break
      }
    }
  }

  const handleConfirmDelete = async () => {
    if (deleteTax) {
      try {
        await deleteTaxMutation.mutateAsync(deleteTax.id)
        setDeleteTax(null)
      } catch (error) {
        toast.error(extractErrorMessage(error))
      }
    }
  }

  const columns: ColumnDef<Tax>[] = [
    {
      accessorKey: 'code',
      header: 'Code',
      cell: ({ row }) => {
        const tax = row.original
        return <div className='font-mono font-medium text-sm'>{tax.code}</div>
      },
      size: 150,
    },
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const tax = row.original
        return <div className='font-medium'>{tax.name}</div>
      },
      size: 200,
    },
    {
      accessorKey: 'tax_type',
      header: 'Type',
      cell: ({ row }) => {
        const tax = row.original
        return (
          <div className='flex items-center gap-2'>
            {tax.tax_type === TAX_TYPE.percentage ? (
              <Percent className='w-4 h-4 text-blue-600' />
            ) : tax.tax_type === TAX_TYPE.fixed ? (
              <DollarSign className='w-4 h-4 text-green-600' />
            ) : (
              <Percent className='w-4 h-4 text-purple-600' />
            )}
            <span className='capitalize'>{tax.tax_type}</span>
          </div>
        )
      },
      size: 140,
    },
    {
      accessorKey: 'rate',
      header: 'Rate',
      cell: ({ row }) => {
        const tax = row.original
        if (
          tax.tax_type === TAX_TYPE.percentage ||
          tax.tax_type === TAX_TYPE.compound
        ) {
          return (
            <div className='font-medium text-blue-600'>
              {tax.percentage_value}%
            </div>
          )
        } else {
          return (
            <div className='font-medium text-green-600'>
              {tax.currency} {((tax.fixed_amount_cents || 0) / 100).toFixed(2)}
            </div>
          )
        }
      },
      size: 120,
    },
    {
      accessorKey: 'jurisdiction',
      header: 'Jurisdiction',
      cell: ({ row }) => {
        const tax = row.original
        return (
          <div className='text-sm text-muted-foreground'>
            {tax.jurisdiction || '-'}
          </div>
        )
      },
      size: 140,
    },
    {
      accessorKey: 'is_active',
      header: 'Status',
      cell: ({ row }) => {
        const tax = row.original
        return (
          <Badge
            variant={tax.is_active ? 'default' : 'secondary'}
            className='text-xs capitalize'
          >
            {tax.is_active ? 'Active' : 'Inactive'}
          </Badge>
        )
      },
      size: 100,
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => {
        const tax = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant='ghost' size='sm'>
                <MoreHorizontal className='w-4 h-4' />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align='end'>
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  handleTaxAction('edit', tax)
                }}
              >
                <Edit className='w-4 h-4 mr-2' />
                Edit
              </DropdownMenuItem>
              {!tax.is_active && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleTaxAction('activate', tax)
                  }}
                >
                  <Play className='w-4 h-4 mr-2' />
                  Activate
                </DropdownMenuItem>
              )}
              {tax.is_active && (
                <DropdownMenuItem
                  onClick={e => {
                    e.stopPropagation()
                    handleTaxAction('deactivate', tax)
                  }}
                >
                  <Pause className='w-4 h-4 mr-2' />
                  Deactivate
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={e => {
                  e.stopPropagation()
                  handleTaxAction('delete', tax)
                }}
                className='text-red-600 focus:text-red-600'
              >
                <Trash2 className='w-4 h-4 mr-2' />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
      size: 50,
    },
  ]

  return (
    <>
      <div className='[&_table]:bg-transparent [&_thead]:bg-transparent [&_tr:hover]:bg-slate-50/50 dark:[&_tr:hover]:bg-slate-800/50'>
        <EnhancedTable
          key='taxes-table'
          data={taxes}
          columns={columns}
          height='calc(100vh - 400px)'
          className='bg-transparent border-0'
          onRowClick={tax => onViewTax?.(tax)}
        />
      </div>

      <ConfirmationDialog
        open={!!deleteTax}
        onOpenChange={open => !open && setDeleteTax(null)}
        title='Delete Tax'
        description={`Are you sure you want to delete "${deleteTax?.name}" (${deleteTax?.code})? This action will mark it as inactive.`}
        confirmText='Delete'
        cancelText='Cancel'
        variant='destructive'
        isLoading={deleteTaxMutation.isPending}
        onConfirm={handleConfirmDelete}
        onCancel={() => setDeleteTax(null)}
      />
    </>
  )
}
